import { Phone, MessageSquareText, Mail, Clock, MapPin, Siren } from "lucide-react";
import { SITE } from "../config/site";
import { PageHero, Reveal, useSeo } from "../components/Reveal";
import { RequestForm } from "../components/RequestForm";

export default function Contact() {
  useSeo(
    "Contact & Request Testing | SwiftTest Solutions — Eastern Iowa",
    "Request mobile testing or reach SwiftTest Solutions for urgent 24/7 dispatch across Eastern Iowa. Call or text for post-accident and last-minute testing."
  );

  return (
    <div data-testid="contact-page">
      <PageHero
        testId="contact-hero"
        eyebrow="Contact"
        title="Let's get your testing scheduled"
        lead="Planned request or urgent dispatch — either way, you're one step from a certified collector at your door."
      />

      <section className="bg-background">
        <div className="mx-auto max-w-7xl px-5 pb-24 pt-16 sm:px-8">
          <div className="grid items-start gap-10 lg:grid-cols-5">
            {/* CONTACT CARD */}
            <Reveal className="lg:col-span-2">
              <div className="space-y-5">
                <div
                  data-testid="contact-urgent-card"
                  className="rounded-3xl border border-amber-200 bg-amber-50 p-7"
                >
                  <span className="inline-flex items-center gap-2 rounded-full bg-amber-500 px-3 py-1 text-xs font-bold uppercase tracking-wider text-white">
                    <Siren className="h-3.5 w-3.5" /> Need Testing Now?
                  </span>
                  <p
                    data-testid="contact-phone-display"
                    className="mt-4 font-display text-3xl font-bold tracking-tight text-slate-900"
                  >
                    {SITE.phoneDisplay}
                  </p>
                  <p className="mt-1.5 text-sm text-amber-900/80">
                    Call or text for instant dispatch — post-accident, reasonable suspicion, and
                    last-minute testing, 24/7.
                  </p>
                  <div className="mt-5 grid grid-cols-2 gap-3">
                    <a
                      data-testid="contact-call-button"
                      href={`tel:${SITE.phoneTel}`}
                      className="flex items-center justify-center gap-2 rounded-full bg-teal-700 px-4 py-3 text-sm font-bold text-white shadow-md transition-all hover:-translate-y-0.5 hover:bg-teal-800"
                    >
                      <Phone className="h-4 w-4" /> Call
                    </a>
                    <a
                      data-testid="contact-text-button"
                      href={`sms:${SITE.phoneSms}`}
                      className="flex items-center justify-center gap-2 rounded-full bg-slate-900 px-4 py-3 text-sm font-bold text-white shadow-md transition-all hover:-translate-y-0.5 hover:bg-slate-700"
                    >
                      <MessageSquareText className="h-4 w-4" /> Text
                    </a>
                  </div>
                </div>

                <div className="space-y-4 rounded-3xl border border-slate-200 bg-white p-7 shadow-sm">
                  <div className="flex items-start gap-3.5">
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-teal-50 text-teal-700">
                      <Mail className="h-5 w-5" />
                    </span>
                    <div>
                      <p className="text-sm font-bold text-slate-900">Email</p>
                      <a
                        data-testid="contact-email-link"
                        href={`mailto:${SITE.email}`}
                        className="text-sm text-teal-700 underline-offset-2 hover:underline"
                      >
                        {SITE.email}
                      </a>
                    </div>
                  </div>
                  <div className="flex items-start gap-3.5">
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-teal-50 text-teal-700">
                      <Clock className="h-5 w-5" />
                    </span>
                    <div>
                      <p className="text-sm font-bold text-slate-900">Hours</p>
                      <p className="text-sm text-slate-600">{SITE.hours}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3.5">
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-teal-50 text-teal-700">
                      <MapPin className="h-5 w-5" />
                    </span>
                    <div>
                      <p className="text-sm font-bold text-slate-900">Location</p>
                      <p data-testid="contact-location-note" className="text-sm text-slate-600">
                        Fully mobile — no walk-in office. We come to you anywhere in Eastern Iowa.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </Reveal>

            {/* FORM */}
            <Reveal delay={0.12} className="lg:col-span-3">
              <RequestForm />
            </Reveal>
          </div>
        </div>
      </section>
    </div>
  );
}
